import { useState, useEffect } from 'react';

interface CookiePreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CookiePreferencesModal({ isOpen, onClose }: CookiePreferencesModalProps) {
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    console.log('[CookieModal] Modal abierto:', isOpen);
    if (isOpen) {
      const saved = localStorage.getItem('cookie_consent');
      if (saved) {
        const parsed = JSON.parse(saved);
        setAnalytics(parsed.analytics || false);
        setMarketing(parsed.marketing || false);
      }
    }
  }, [isOpen]);

  const handleSave = () => {
    console.log('[CookieModal] Guardando preferencias:', { analytics, marketing });
    const consent = {
      necessary: true,
      analytics,
      marketing,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consent));
    onClose();
  };

  const handleRejectAll = () => {
    console.log('[CookieModal] Rechazando todas');
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consent));
    onClose();
  };

  const handleAcceptAll = () => {
    console.log('[CookieModal] Aceptando todas');
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consent));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 z-[9998]"
        onClick={onClose}
      />
      <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4 pointer-events-none">
        <div 
          className="bg-white rounded-lg shadow-2xl max-w-[500px] w-full max-h-[90vh] overflow-y-auto pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[20px] font-bold text-[#222222]">Preferencias de cookies</h2>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F0EB] transition-colors cursor-pointer"
                aria-label="Cerrar"
              >
                <i className="ri-close-line text-[20px] text-[#222222]"></i>
              </button>
            </div>

            <p className="text-[14px] text-[#6B6860] mb-6">
              Utilizamos cookies para mejorar tu experiencia. Puedes elegir qué categorías aceptar.
            </p>

            <div className="space-y-4 mb-6">
              <div className="flex items-start justify-between gap-4 p-4 bg-[#F5F0EB] rounded-lg">
                <div className="flex-1">
                  <h3 className="text-[15px] font-semibold text-[#222222] mb-1">Necesarias</h3>
                  <p className="text-[13px] text-[#6B6860]">
                    Esenciales para el funcionamiento del sitio web.
                  </p>
                </div>
                <div className="flex items-center">
                  <div className="w-12 h-6 bg-[#EBCFD4] rounded-full flex items-center px-1 cursor-not-allowed opacity-60">
                    <div className="w-4 h-4 bg-white rounded-full ml-auto shadow-sm"></div>
                  </div>
                </div>
              </div>

              <div className="flex items-start justify-between gap-4 p-4 border border-[#E8DED2] rounded-lg">
                <div className="flex-1">
                  <h3 className="text-[15px] font-semibold text-[#222222] mb-1">Analíticas</h3>
                  <p className="text-[13px] text-[#6B6860]">
                    Nos ayudan a entender cómo usas el sitio web.
                  </p>
                </div>
                <button
                  onClick={() => {
                    console.log('[CookieModal] Toggle analytics:', !analytics);
                    setAnalytics(!analytics);
                  }}
                  className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors cursor-pointer ${
                    analytics ? 'bg-[#EBCFD4]' : 'bg-[#E8DED2]'
                  }`}
                  aria-label="Toggle analíticas"
                >
                  <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
                    analytics ? 'ml-auto' : ''
                  }`}></div>
                </button>
              </div>

              <div className="flex items-start justify-between gap-4 p-4 border border-[#E8DED2] rounded-lg">
                <div className="flex-1">
                  <h3 className="text-[15px] font-semibold text-[#222222] mb-1">Marketing</h3>
                  <p className="text-[13px] text-[#6B6860]">
                    Para rastrear enlaces de afiliación y medir efectividad.
                  </p>
                </div>
                <button
                  onClick={() => {
                    console.log('[CookieModal] Toggle marketing:', !marketing);
                    setMarketing(!marketing);
                  }}
                  className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors cursor-pointer ${
                    marketing ? 'bg-[#EBCFD4]' : 'bg-[#E8DED2]'
                  }`}
                  aria-label="Toggle marketing"
                >
                  <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
                    marketing ? 'ml-auto' : ''
                  }`}></div>
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleRejectAll}
                className="flex-1 px-4 py-3 text-[14px] font-medium text-[#222222] bg-white border border-[#E8DED2] rounded-lg hover:bg-[#F5F0EB] transition-colors cursor-pointer whitespace-nowrap"
              >
                Rechazar todo
              </button>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-3 text-[14px] font-medium text-[#222222] bg-[#F5F0EB] rounded-lg hover:bg-[#E8DED2] transition-colors cursor-pointer whitespace-nowrap"
              >
                Guardar
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-4 py-3 text-[14px] font-medium text-white bg-[#222222] rounded-lg hover:bg-[#3A3A3A] transition-colors cursor-pointer whitespace-nowrap"
              >
                Aceptar todo
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
